import { Call, Email, LocationOn, Print, StayCurrentPortrait } from '@mui/icons-material';
import { Box } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Prefixes, Suffixes } from '../../../enums/ContactEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  ICompany,
  IContact,
  ITitleInternal,
  useCompaniesNamesLazyQuery,
  useTitlesInternalLazyQuery,
  useUpdateContactDetailsMutation
} from '../../../generated/graphql';
import { fullNameExists, sameContactEmailExists } from '../../../utils/clientUtils';
import { canUserEdit } from '../../../utils/rightUtils';
import { getLabelColor, getRequiredFieldLabelColor } from '../../../utils/styleUtils';
import { Colors } from '../../../utils/theme';
import { validEmailRegex } from '../../../utils/ui';
import CancelButton from '../../Common/Buttons/CancelButton';
import EditButton from '../../Common/Buttons/EditButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../Common/Fields/InputComponent';
import Loader from '../../Common/Loader';
import { IErrors, ErrorTexts } from '../NewContact';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

interface IProps {
  contact: IContact;
}
const defaultErrorsObj: IErrors = {
  firstName: null,
  lastName: null,
  titleInternal: null,
  company: null,
  email: null,
  updateError: undefined,
  interests: null
};

const ContactGeneralTab: React.FunctionComponent<IProps> = ({ contact }) => {
  const [getTitlesInternal, titlesInternal] = useTitlesInternalLazyQuery();
  const [getCompaniesNames, companiesNames] = useCompaniesNamesLazyQuery();
  const [updateContactDetails, updateContactDetailsResult] = useUpdateContactDetailsMutation();
  const [editable, setEditable] = useState(false);
  const [companiesNamesOptions, setCompaniesNamesOptions] = useState([]);
  const [titlesInternalOptions, setTitlesInternalOptions] = useState([]);
  const [prefix, setPrefix] = useState(contact.prefix || null);
  const [suffix, setSuffix] = useState(contact.suffix || null);
  const [titleInternal, setTitleInternal] = useState<ITitleInternal | null>(contact.titleInternal);
  const [company, setCompany] = useState<ICompany | null>(contact.company);
  const [firstName, setFirstName] = useState(contact.first_name);
  const [middleName, setMiddleName] = useState(contact.middle_name || '');
  const [lastName, setLastName] = useState(contact.last_name);
  const [informalName, setInformalName] = useState(contact.informal_name || '');
  const [titleExternal, setTitleExternal] = useState(contact.title_external || '');
  const [notes, setNotes] = useState(contact.notes || '');
  const [phoneDirect, setPhoneDirect] = useState(contact.phone_direct || '');
  const [mobilePhone, setMobilePhone] = useState(contact.phone_mobile || '');
  const [faxDirect, setFaxDirect] = useState(contact.fax || '');
  const [email, setEmail] = useState(contact.email || '');
  const [location, setLocation] = useState(contact.location_of_contact || '');
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [linkedIn, setLinkedIn] = useState(contact.linkedin || '');
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Contact);

  useEffect(() => {
    if (titlesInternal.data && titlesInternal.data.result) {
      setTitlesInternalOptions(titlesInternal.data.result as never[]);
    }
  }, [titlesInternal]);

  useEffect(() => {
    if (companiesNames.data && companiesNames.data.result) {
      setCompaniesNamesOptions(companiesNames.data.result as never[]);
    }
  }, [companiesNames]);

  const handleEditClicked = () => {
    if (companiesNamesOptions.length === 0 || titlesInternalOptions.length === 0) {
      getTitlesInternal();
      getCompaniesNames();
    }
    setEditable(true);
  };

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        firstName: ErrorTexts.firstName
      });
    } else {
      setFormErrors({ ...formErrors, firstName: null });
    }
    setFirstName(value);
  };

  const handleLastNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        lastName: ErrorTexts.lastName
      });
    } else {
      setFormErrors({ ...formErrors, lastName: null });
    }
    setLastName(value);
  };

  const handleTitleInternalChange = (
    _: unknown,
    newValue: React.SetStateAction<ITitleInternal | null>
  ) => {
    if (newValue) {
      setFormErrors({ ...formErrors, titleInternal: null });
    } else {
      setFormErrors({ ...formErrors, titleInternal: ErrorTexts.titleInternal });
    }
    setTitleInternal(newValue);
  };

  const handleCompanyChange = (_: unknown, newValue: React.SetStateAction<ICompany | null>) => {
    if (newValue) {
      setFormErrors({ ...formErrors, company: null });
    } else {
      setFormErrors({ ...formErrors, company: ErrorTexts.company });
    }
    setCompany(newValue);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    const isValidEmail = value.match(validEmailRegex);
    if (!isValidEmail) {
      setFormErrors({
        ...formErrors,
        email: ErrorTexts.email
      });
    } else {
      setFormErrors({ ...formErrors, email: null });
    }
    setEmail(value);
  };

  const getFullName = () => {
    const result = [];
    if (prefix) {
      result.push(prefix);
    }
    if (firstName) {
      result.push(firstName);
    }
    if (middleName) {
      result.push(middleName);
    }
    if (lastName) {
      result.push(lastName);
    }
    if (suffix) {
      result.push(suffix);
    }
    return result.join(' ');
  };

  const handleCancel = () => {
    setPrefix(contact.prefix || null);
    setSuffix(contact.suffix || null);
    setTitleInternal(contact.titleInternal);
    setCompany(contact.company);
    setFirstName(contact.first_name);
    setMiddleName(contact.middle_name || '');
    setLastName(contact.last_name);
    setInformalName(contact.informal_name || '');
    setTitleExternal(contact.title_external || '');
    setNotes(contact.notes || '');
    setPhoneDirect(contact.phone_direct || '');
    setMobilePhone(contact.phone_mobile || '');
    setFaxDirect(contact.fax || '');
    setEmail(contact.email || '');
    setLocation(contact.location_of_contact || '');
    setEditable(false);
    setFormErrors(defaultErrorsObj);
    setLinkedIn(contact.linkedin || '');
  };

  const handleSave = () => {
    const errors = {
      company: null,
      email: null,
      firstName: null,
      lastName: null,
      titleInternal: null
    } as IErrors;

    let hasErrors = false;
    if (!company) {
      hasErrors = true;
      errors.company = ErrorTexts.company;
    }
    if (!titleInternal) {
      hasErrors = true;
      errors.titleInternal = ErrorTexts.titleInternal;
    }
    if (!firstName || firstName.length < 2) {
      hasErrors = true;
      errors.firstName = ErrorTexts.firstName;
    }
    if (!lastName || lastName.length < 2) {
      hasErrors = true;
      errors.lastName = ErrorTexts.lastName;
    }
    if (email) {
      if (contact.email !== email) {
        const isValidEmail = email.match(validEmailRegex);
        if (!isValidEmail) {
          hasErrors = true;
          errors.email = ErrorTexts.email;
        }
        // Email uniqueness is checked on the backend - frontend cache check is unreliable with filtered/paginated queries
      }
    } else {
      hasErrors = true;
      errors.email = ErrorTexts.email;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const isNameChanged = isContactNameAndCompanyChanged();
      const nameExists = isNameChanged
        ? fullNameExists(firstName, lastName, company?.name as string)
        : false;
      if (nameExists) {
        dispatch({
          type: Actions.ShowConfirmation,
          payload: {
            text: 'There is a contact with the same first and last name. Are you sure you want to continue?',
            agreeAction: submitContact
          }
        });
      } else {
        submitContact();
      }
    }
  };

  const isContactNameAndCompanyChanged = () => {
    if (contact.first_name !== firstName) {
      return true;
    }
    if (contact.last_name !== lastName) {
      return true;
    }
    if (contact.company.id !== company?.id) {
      return true;
    }
    return false;
  };

  const submitContact = () => {
    dispatch({ type: Actions.HideConfirmation });
    updateContactDetails({
      variables: {
        id: parseInt(contact.id),
        input: {
          prefix,
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          suffix,
          informal_name: informalName,
          title_internal_id: parseInt(titleInternal?.id as string),
          title_external: titleExternal,
          company_id: parseInt(company?.id as string),
          notes,
          phone_direct: phoneDirect,
          phone_mobile: mobilePhone,
          fax: faxDirect,
          email: email as string,
          location_of_contact: location,
          linkedin: linkedIn
        }
      },
      update(_, { data }) {
        if (data && data.result) {
          setEditable(false);
          dispatch({
            type: Actions.SetSelectedContact,
            payload: {
              name: data.result.fullName,
              company_name: data.result.company.name
            }
          });
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Successfully updated ${data.result.fullName}`,
              severity: 'success',
              autoHide: 2000
            }
          });
        }
      }
    }).catch((e) => setFormErrors({ ...formErrors, updateError: e.message.slice(15) }));
  };

  return (
    <Box sx={{ padding: '80px', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-general-edit-button"
          onClick={handleEditClicked}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gap: '60px'
        }}
      >
        <Box sx={{ gridColumn: 'span 3', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <AutocompleteComponent
            id="contact-general-prefix"
            label="Prefix"
            options={Prefixes}
            getOptionLabel={(option: string) => option}
            disabled={!editable}
            values={prefix}
            onChange={(_, value) => setPrefix(value)}
          />
          <InputComponent
            id="contact-general-first-name"
            label="First Name"
            value={firstName}
            onChange={handleFirstNameChange}
            disabled={!editable}
            errorMsg={formErrors.firstName}
          />
          <InputComponent
            id="contact-general-middle-name"
            label="Middle Name"
            value={middleName}
            onChange={(e) => setMiddleName(e.currentTarget.value)}
            disabled={!editable}
          />
          <InputComponent
            id="contact-general-last-name"
            label="Last Name"
            value={lastName}
            onChange={handleLastNameChange}
            disabled={!editable}
            errorMsg={formErrors.lastName}
          />
          <AutocompleteComponent
            id="contact-general-suffix"
            label="Suffix"
            options={Suffixes}
            getOptionLabel={(option: string) => option}
            disabled={!editable}
            values={suffix}
            onChange={(_, value) => setSuffix(value)}
          />
          <InputComponent
            id="contact-general-informal-name"
            label="Informal Name"
            value={informalName}
            onChange={(e) => setInformalName(e.currentTarget.value)}
            disabled={!editable}
          />
        </Box>
        <Box sx={{ gridColumn: 'span 5', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <AutocompleteComponent
            id="contact-general-title-internal"
            label="Title external"
            options={titlesInternalOptions}
            getOptionLabel={(option: ITitleInternal) => option?.name}
            disabled={!editable}
            values={titleInternal}
            onChange={handleTitleInternalChange}
            errorMsg={formErrors.titleInternal}
          />
          <InputComponent
            id="contact-general-title-external"
            label="Title external"
            value={titleExternal}
            onChange={(e) => setTitleExternal(e.currentTarget.value)}
            disabled={!editable}
          />
          <AutocompleteComponent
            id="contact-general-company"
            label="Choose company"
            options={companiesNamesOptions}
            getOptionLabel={(option: ICompany) => option?.name}
            disabled={!editable}
            values={company}
            onChange={handleCompanyChange}
            errorMsg={formErrors.company}
          />
          <InputComponent
            id="contact-general-full-name"
            label="Full Name"
            value={getFullName()}
            disabled={true}
          />
          <InputComponent
            id="contact-general-notes"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            disabled={!editable}
            multiline={true}
            rows={4}
          />
        </Box>
        <Box sx={{ gridColumn: 'span 4', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <InputComponent
            id="contact-general-phone-direct"
            label="Phone (direct)"
            value={phoneDirect}
            onChange={(e) => setPhoneDirect(e.currentTarget.value)}
            disabled={!editable}
            startAdornment={
              <Call
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(editable)
                }}
              />
            }
          />
          <InputComponent
            id="contact-general-mobile-phone"
            label="Mobile phone"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.currentTarget.value)}
            disabled={!editable}
            startAdornment={
              <StayCurrentPortrait
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(editable)
                }}
              />
            }
          />
          <InputComponent
            id="contact-general-fax-direct"
            label="Fax (direct)"
            value={faxDirect}
            onChange={(e) => setFaxDirect(e.currentTarget.value)}
            disabled={!editable}
            startAdornment={
              <Print
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(editable)
                }}
              />
            }
          />
          <InputComponent
            id="contact-general-email"
            label="Email (direct)"
            value={email}
            onChange={handleEmailChange}
            disabled={!editable}
            errorMsg={formErrors.email}
            startAdornment={
              <Email
                sx={{
                  marginRight: '13px',
                  color: getRequiredFieldLabelColor(editable, formErrors.email)
                }}
              />
            }
          />
          <InputComponent
            id="contact-general-location"
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.currentTarget.value)}
            disabled={!editable}
            startAdornment={
              <LocationOn
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(editable)
                }}
              />
            }
          />
          <InputComponent
            id="contact-general-linkedIn"
            label="LinkedIn"
            value={linkedIn}
            onChange={(e) => setLinkedIn(e.currentTarget.value)}
            disabled={!editable}
            startAdornment={
              <LinkedInIcon
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(editable)
                }}
              />
            }
          />
        </Box>
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '60px' }}>
          <CancelButton
            id="cancel-new-contact-button"
            disabled={!editable}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="contact-general-save-button"
            disabled={!editable}
            onClick={handleSave}
            text="Save"
          />
        </Box>
      )}
      <Loader loading={updateContactDetailsResult.loading} />
    </Box>
  );
};

export default ContactGeneralTab;
